"use client";

import { useState } from "react";
import Link from "next/link";
import Button from "../ui/Button";
import Image from "next/image";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";
const DEFAULT_EMAIL_ERROR = "Please enter a valid email address.";

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export type JoinTheFamSectionProps = {
  heading?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  /**
   * When set, replaces the CTA button with the name/email lead-capture form
   * (POST /api/email-leads with this `source`), Dank Club welcome-gift style.
   */
  leadFormSource?: string;
};

function LeadCaptureForm({ source }: { source: string }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !loading && form.email.trim().length > 0;

  return (
    <>
      <form
        className="space-y-3 max-w-md"
        onSubmit={async (event) => {
          event.preventDefault();
          setError(null);
          setSuccess(false);

          if (!isValidEmail(form.email)) {
            setError(DEFAULT_EMAIL_ERROR);
            return;
          }

          setLoading(true);
          try {
            const response = await fetch(`${API_BASE}/api/email-leads`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: form.email.trim(),
                name: form.name.trim() || undefined,
                source,
              }),
            });

            const data = await response.json();
            if (data?.success) {
              setSuccess(true);
              setForm({ name: "", email: "" });
              return;
            }
            setError(data?.error || "Failed to submit. Please try again.");
          } catch (submitError: unknown) {
            setError(
              submitError instanceof Error ? submitError.message : "Failed to submit. Please try again."
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        <label htmlFor={`join-the-fam-name-${source}`} className="sr-only">
          Your name
        </label>
        <input
          id={`join-the-fam-name-${source}`}
          name="name"
          type="text"
          value={form.name}
          onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          placeholder="Your name"
          autoComplete="given-name"
          className="h-12 w-full rounded-xl border border-neutral-10 bg-constant-white px-4 text-body-md text-neutral-80 outline-none transition placeholder:text-neutral-40 focus:border-blue-70"
        />
        <label htmlFor={`join-the-fam-email-${source}`} className="sr-only">
          Your email
        </label>
        <input
          id={`join-the-fam-email-${source}`}
          name="email"
          type="email"
          value={form.email}
          onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
          placeholder="Your email"
          required
          autoComplete="email"
          className="h-12 w-full rounded-xl border border-neutral-10 bg-constant-white px-4 text-body-md text-neutral-80 outline-none transition placeholder:text-neutral-40 focus:border-blue-70"
        />
        <Button
          type="submit"
          variant="glass"
          size="lg"
          disabled={!canSubmit}
          className="w-full rounded-xl bg-yellow-50 text-black"
        >
          {loading ? "Submitting..." : "Join now"}
        </Button>
      </form>

      {success ? (
        <p className="mt-3 text-body-12-desktop text-constant-white/90">
          Thanks! Check your inbox for your welcome gift.
        </p>
      ) : null}
      {error ? <p className="mt-3 text-body-12-desktop text-red-100">{error}</p> : null}
    </>
  );
}

export default function JoinTheFamSection({
  heading = "JOIN THE FAM",
  description = "At the Dank Cannabis store locations, we believe in rewarding those who shop often with us. Join our Dank club today and start earning discounts for future purchases!",
  // The copy pitches Dank Club, so the button has to land on Dank Club. It used
  // to say "Order & Pickup" and drop people on the product grid.
  ctaLabel = "Join Dank Club",
  ctaHref = "/dank-club",
  leadFormSource,
}: JoinTheFamSectionProps = {}) {
  return (
    <section className="w-full px-4 sm:px-8" aria-label="Join Dank Club">
      <div className="relative w-full rounded-2xl overflow-hidden bg-blue-80 max-sm:h-[580px]">
        {/* Background Shape */}
        <div className="absolute inset-0 max-sm:hidden">
          <Image
            src="/media/sections/join-the-fam/backgrounds/background-shape.svg"
            alt=""
            className="absolute object-cover top-0 left-1/2 translate-x-[-50%] w-full h-full"
            priority={false}
            loading="lazy"
            width={1920}
            height={415}
          />
        </div>

        {/* Background Image - Cannabis Buds */}
        <div className="absolute inset-0 max-sm:hidden">
          <Image
            src="/media/sections/join-the-fam/backgrounds/background-image.webp"
            alt=""
            className="absolute object-cover top-0 right-0 h-full"
            priority={false}
            loading="lazy"
            width={520}
            height={477}
          />
        </div>

        {/* Background Image - Cannabis Buds */}
        <div className="absolute bottom-0 right-0 sm:hidden w-full h-full">
          <Image
            src="/media/sections/join-the-fam/backgrounds/background-image-mobile.webp"
            alt=""
            className="object-cover w-full max-sm:h-full"
            priority={false}
            loading="lazy"
            width={328}
            height={580}
          />
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col lg:flex-row items-center">
          {/* Left Side - Text Content */}
          <div className="flex-1 px-6 lg:px-12 py-8 lg:py-12 flex flex-col justify-center">
            <h2 className="sm:text-h3-desktop text-h3-mobile font-bold text-constant-white mb-4 lg:mb-6 leading-tight">
              {heading}
            </h2>
            <p className="sm:text-body-16-desktop text-body-14-mobile text-constant-white/95 mb-6 lg:mb-8 leading-relaxed max-w-2xl">
              {description}
            </p>
            {leadFormSource ? (
              <LeadCaptureForm source={leadFormSource} />
            ) : (
              <Button asChild variant="bordered" color="white" size="lg" className="rounded-xl px-6 py-3 w-fit">
                <Link href={ctaHref}>
                  {ctaLabel}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
